import type { BoardCaregiver, ScheduleBoard, VisitSummary } from '@homecare/contracts';
import { addDays, toLocalDate } from '@homecare/domain';
import type { StatusTone } from '@homecare/ui';

/**
 * What the board draws, worked out from what the API sent — pure, so the
 * rules below are unit-tested rather than eyeballed.
 *
 * ## One visit, two cards
 *
 * An overnight appears on both days it touches (slice-03 §8). The first card
 * is the visit; the second says it is the same visit carrying on, and says
 * which day it started. So the number of cards is not the number of shifts
 * (RECOVERY §2) — anything counting "shifts this week" counts `visits`, not
 * cards.
 *
 * Cards are placed by the API's `localDate`. The board never works out a day
 * from an instant: that is the server's job, done in the service location's
 * zone, and doing it again here would be doing it in the browser's.
 */

export interface BoardCard {
  visit: VisitSummary;
  /** `starts` on its own day; `continues` on the morning after an overnight. */
  part: 'starts' | 'continues';
}

export interface BoardRow {
  /** Null for the open-shifts lane. */
  caregiver: BoardCaregiver | null;
  /** One list per day of `days`, in time order. */
  cells: BoardCard[][];
}

export interface BoardLayout {
  days: string[];
  /** Pinned first: unfilled work is what a coordinator is looking for. */
  open: BoardRow;
  rows: BoardRow[];
  /** Distinct visits, as opposed to cards. */
  shifts: number;
  cards: number;
}

export function layoutBoard(
  board: Pick<ScheduleBoard, 'days' | 'caregivers' | 'visits'>,
): BoardLayout {
  const dayIndex = new Map(board.days.map((day, at) => [day, at]));
  const empty = (): BoardCard[][] => board.days.map(() => []);

  const open: BoardRow = { caregiver: null, cells: empty() };
  const rows = new Map<string, BoardRow>(
    board.caregivers.map((caregiver) => [caregiver.id, { caregiver, cells: empty() }]),
  );

  let cards = 0;
  for (const visit of board.visits) {
    const row = visit.caregiverId ? rowFor(rows, visit, empty) : open;
    const place = (day: string, part: BoardCard['part']): void => {
      const at = dayIndex.get(day);
      if (at === undefined) return;
      row.cells[at]?.push({ visit, part });
      cards += 1;
    };
    place(visit.localDate, 'starts');
    if (visit.crossesMidnight) place(addDays(toLocalDate(visit.localDate), 1), 'continues');
  }

  // A continuation is the earliest thing on its morning, so it sorts first.
  const order = (a: BoardCard, b: BoardCard): number =>
    a.part === b.part
      ? a.visit.startsAt.localeCompare(b.visit.startsAt)
      : a.part === 'continues'
        ? -1
        : 1;
  for (const row of [open, ...rows.values()]) for (const cell of row.cells) cell.sort(order);

  return { days: board.days, open, rows: [...rows.values()], shifts: board.visits.length, cards };
}

/**
 * The API lists everyone holding a visit in the week, so this is a guard, not
 * a path: a card is never dropped because its row was missing.
 */
function rowFor(
  rows: Map<string, BoardRow>,
  visit: VisitSummary,
  empty: () => BoardCard[][],
): BoardRow {
  const id = visit.caregiverId ?? '';
  let row = rows.get(id);
  if (!row) {
    row = {
      caregiver: { id, name: visit.caregiverName ?? 'Unnamed caregiver', status: 'UNKNOWN' },
      cells: empty(),
    };
    rows.set(id, row);
  }
  return row;
}

// ---------------------------------------------------------------------------
// Status: a glyph and a word, never colour alone
// ---------------------------------------------------------------------------

export interface BoardState {
  tone: StatusTone;
  label: string;
}

/**
 * What a card says it is. Open and called-out are about the assignment, not
 * the state machine — a called-out visit is still SCHEDULED (§14.5) — so they
 * are read from the caregiver and the flags before the status.
 */
export function boardState(
  visit: Pick<VisitSummary, 'status' | 'caregiverId' | 'riskFlags'>,
): BoardState {
  switch (visit.status) {
    case 'CANCELLED':
      return { tone: 'inactive', label: 'Cancelled' };
    case 'MISSED':
      return { tone: 'critical', label: 'Missed' };
    case 'REJECTED':
      return { tone: 'critical', label: 'Rejected' };
    case 'IN_PROGRESS':
      return { tone: 'progress', label: 'In progress' };
    case 'COMPLETED':
      return { tone: 'verified', label: 'Completed' };
    case 'VERIFIED':
      return { tone: 'verified', label: 'Verified' };
    case 'NEEDS_REVIEW':
      return { tone: 'warning', label: 'Needs review' };
    case 'SCHEDULED':
      if (!visit.caregiverId) {
        return visit.riskFlags.includes('CALLOUT')
          ? { tone: 'critical', label: 'Called out' }
          : { tone: 'warning', label: 'Open' };
      }
      return visit.riskFlags.includes('AT_RISK')
        ? { tone: 'warning', label: 'At risk' }
        : { tone: 'scheduled', label: 'Scheduled' };
  }
}

/** The legend: every state a card on this board can be in this slice. */
export const LEGEND: readonly BoardState[] = [
  { tone: 'scheduled', label: 'Scheduled' },
  { tone: 'warning', label: 'Open' },
  { tone: 'critical', label: 'Called out' },
  { tone: 'warning', label: 'At risk' },
  { tone: 'progress', label: 'In progress' },
  { tone: 'verified', label: 'Completed' },
];
